import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'sonner';
import { useSelector } from 'react-redux';
import type { RootState } from './store';
import { useAuth } from './hooks/useAuth';
import { PreferencesProvider, usePreferences } from './contexts/PreferencesContext';
import { rolesOfUser, Role } from './types/practiceArea';
import { launchCorporateSuite } from './services/corporateApi';
import { signOut as firebaseSignOut } from './utils/firebase';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import ColumnMark from './components/chambers/ColumnMark';

// Layout components (always loaded)
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import GlobalPaywallHost from './components/billing/GlobalPaywallHost';
import CommandPalette from './components/ui/CommandPalette';
import ChatPanel from './components/chat/ChatPanel';
import LoadingSpinner from './components/common/LoadingSpinner';
import SealLoader from './components/auth/SealLoader';
import MatterContextBanner from './components/workspace/MatterContextBanner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { NotFoundPage, RoleGateNotice } from './components/common/RouteNotices';

// Onboarding
import SignupWizard from './components/onboarding/SignupWizard';

// Auth components
import LoginForm from './components/auth/LoginForm';
import LegalGate from './components/auth/LegalGate';
import PracticeAreaChooser, {
  hasChosenPracticeAreaThisSession,
  canRestoreSameSignInRole,
  restoreSameSignInRole,
} from './components/auth/PracticeAreaChooser';
import { ROLE_LENS } from './utils/activeRole';
import AdminRoute from './components/auth/AdminRoute';
import SupportRoute from './components/auth/SupportRoute';
import { nextGate } from './utils/appGate';
import TrialGate, {
  TrialGateRole,
  ensureTrialOnServer,
} from './components/onboarding/TrialGate';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const ShippingPolicyPage = lazy(() => import('./pages/ShippingPolicyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PUBLIC_POLICY_PATHS = new Set(['/terms', '/privacy', '/refund-policy', '/shipping-policy', '/contact']);
const ClauseBrowser = lazy(() => import('./pages/ClauseBrowser'));
const SectionViewer = lazy(() => import('./pages/SectionViewer'));
const CaseSearchPage = lazy(() => import('./pages/CaseSearchPage'));
const IPCBNSMapperPage = lazy(() => import('./pages/IPCBNSMapperPage'));
const CaseTheoryListPage = lazy(() => import('./pages/CaseTheoryListPage'));
const CaseTheoryBuilderPage = lazy(() => import('./pages/CaseTheoryBuilderPage'));
const ConstitutionPage = lazy(() => import('./pages/ConstitutionPage'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CostUserProfilePage = lazy(() => import('./pages/admin/CostUserProfilePage'));
const SupportDeskPage = lazy(() => import('./pages/SupportDeskPage'));
const SupportShell = lazy(() => import('./components/support/SupportShell'));
const CasePortfolioPage = lazy(() => import('./pages/CasePortfolioPage'));
const MatterChatPage = lazy(() => import('./pages/MatterChatPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const BriefBankPage = lazy(() => import('./pages/BriefBankPage'));
const DocumentEditorPage = lazy(() => import('./pages/DocumentEditorPage'));
const LegalAidPage = lazy(() => import('./pages/LegalAidPage'));
const RightsGuidePage = lazy(() => import('./pages/RightsGuidePage'));
const BailCalculatorPage = lazy(() => import('./pages/BailCalculatorPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const FIRAnalyzerPage = lazy(() => import('./pages/FIRAnalyzerPage'));
const SampleMatterPage = lazy(() => import('./pages/SampleMatterPage'));
const NoticeAnalyzerPage = lazy(() => import('./pages/NoticeAnalyzerPage'));
const ForumCalculatorPage = lazy(() => import('./pages/ForumCalculatorPage'));
const InterestCalculatorPage = lazy(() => import('./pages/InterestCalculatorPage'));
const CivilGuidePage = lazy(() => import('./pages/CivilGuidePage'));
const CauseListPage = lazy(() => import('./pages/CauseListPage'));
const BringYourCasesPage = lazy(() => import('./pages/BringYourCasesPage'));
const StartHerePage = lazy(() => import('./pages/StartHerePage'));
const HearingPrepPage = lazy(() => import('./pages/HearingPrepPage'));
const CaseDiaryPage = lazy(() => import('./pages/CaseDiaryPage'));
const AdvocateDiaryPage = lazy(() => import('./pages/AdvocateDiaryPage'));
const CriminalCalendarPage = lazy(() => import('./pages/CriminalCalendarPage'));
const DocumentVaultPage = lazy(() => import('./pages/DocumentVaultPage'));
const ResearchPage = lazy(() => import('./pages/ResearchPage'));
const ClientWorkspacePage = lazy(() => import('./pages/ClientWorkspacePage'));
const ClientsListPage = lazy(() => import('./pages/ClientsListPage'));
const NewClientPage = lazy(() => import('./pages/NewClientPage'));
const MatterDetailPage = lazy(() => import('./pages/MatterDetailPage'));
const NewMatterPage = lazy(() => import('./pages/NewMatterPage'));

const pageVariants = {
  initial: { opacity: 0, y: 6, scale: 0.995 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -6, scale: 0.995 },
};

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
    transition={{ duration: 0.25, ease: [0.33, 1, 0.68, 1] }}
  >
    {children}
  </motion.div>
);

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <LoadingSpinner message="Loading page..." />
  </div>
);

/**
 * Frontend route guard for role-scoped workspaces (UX belt-and-braces).
 *
 * If the session has an active role and it doesn't match this route's role,
 * show a notice naming the tool's workspace (with a switch) instead of a raw 403 for an off-role deep link
 * (e.g. a criminal-session lawyer hitting `/notice-analyzer`). The backend
 * `require_role` is the REAL enforcement — this only smooths the UX.
 *
 * When no active role is set (0-role / "All areas" session), routes are NOT
 * gated — the union is shown, matching PracticeAreaGate's fall-through.
 */
function RoleRoute({
  role,
  roles,
  children,
}: {
  role?: Role;
  /** Several roles may reach this page. Use for surfaces that genuinely serve
   *  more than one practice — the Calendar shell is shared between criminal and
   *  civil (a closed registry is closed for everyone) even though the RANKING
   *  behind it differs per board. Mirrors `require_any_role` on the backend;
   *  gating here with a single role sent civil users to "/" while the backend
   *  happily served them, so the page was unreachable. */
  roles?: Role[];
  children: React.ReactNode;
}) {
  let active: string | null = null;
  try {
    active = sessionStorage.getItem('nyayabodh_active_role');
  } catch {
    /* storage unavailable — don't trap the user; fall through to the element */
  }
  const allowed = roles ?? (role ? [role] : []);
  if (active && allowed.length > 0 && !allowed.includes(active as Role)) {
    // Say why, and offer the switch — a silent redirect to "/" read as the
    // app ignoring the click (QA Q29).
    return <RoleGateNotice allowed={allowed} />;
  }
  return <>{children}</>;
}

export function AppLayout() {
  const { collapsed } = useSidebar();
  const location = useLocation();
  // Chamber design sweep: the parchment canvas is now app-wide (fixed chambers
  // palette — no dark: variant). Every route sits on the same cream ground as
  // the home dashboard so the whole app reads as one chamber. The full-bleed
  // negative-margin canvas on '/' (LawyerDashboard) still cancels this wrapper's
  // padding; the parchment shell here fills the >1400px gutters on every page.
  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <Sidebar />
      <CommandPalette />
      <ChatPanel />
      <GlobalPaywallHost />
      <main
        className={`pt-14 pb-24 lg:pb-0 transition-all duration-300 relative bg-parchment ${
          collapsed ? 'lg:ml-[72px]' : 'lg:ml-64'
        }`}
      >
        {/* Clean background */}
        <div className="relative max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
          {/* Matter context banner — renders when ?matter_id= is in the URL.
              Shows breadcrumb + back-to-matter link above any tool page so the
              lawyer never loses context when deep-linked from a matter. */}
          <MatterContextBanner />
          {/* Page-level boundary: a crash in one page shows a compact inline
              card here while the header + sidebar stay up. Reset on route
              change. The full-screen boundary in index.tsx stays as the last
              resort for crashes in the shell itself. */}
          <ErrorBoundary variant="inline" resetKey={location.pathname}>
          <Suspense fallback={<PageFallback />}>
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<PageWrapper><Dashboard /></PageWrapper>} />
                <Route path="/start-here" element={<RoleRoute role="criminal"><PageWrapper><StartHerePage /></PageWrapper></RoleRoute>} />
                <Route path="/clauses" element={<PageWrapper><ClauseBrowser /></PageWrapper>} />
                <Route path="/clauses/:act/:section" element={<PageWrapper><SectionViewer /></PageWrapper>} />
                <Route path="/cases" element={<PageWrapper><CaseSearchPage /></PageWrapper>} />
                <Route path="/mappings" element={<PageWrapper><IPCBNSMapperPage /></PageWrapper>} />
                <Route path="/constitution" element={<PageWrapper><ConstitutionPage /></PageWrapper>} />
                <Route path="/theories" element={<RoleRoute roles={['criminal', 'civil']}><PageWrapper><CaseTheoryListPage /></PageWrapper></RoleRoute>} />
                <Route path="/theory/:id" element={<PageWrapper><CaseTheoryBuilderPage /></PageWrapper>} />
                <Route path="/prepare" element={<Navigate to="/theories" replace />} />
                <Route path="/profile" element={<PageWrapper><Profile /></PageWrapper>} />

                <Route path="/portfolio" element={<PageWrapper><CasePortfolioPage /></PageWrapper>} />
                <Route path="/matters/:matterId/chat" element={<PageWrapper><MatterChatPage /></PageWrapper>} />
                <Route path="/templates" element={<PageWrapper><DocumentTemplatesPage /></PageWrapper>} />
                <Route path="/brief-bank" element={<PageWrapper><BriefBankPage /></PageWrapper>} />
                <Route path="/documents/:id/edit" element={<PageWrapper><DocumentEditorPage /></PageWrapper>} />
                <Route path="/legal-aid" element={<PageWrapper><LegalAidPage /></PageWrapper>} />
                <Route path="/rights" element={<PageWrapper><RightsGuidePage /></PageWrapper>} />
                <Route path="/rights/:slug" element={<PageWrapper><RightsGuidePage /></PageWrapper>} />
                <Route path="/bail" element={<RoleRoute role="criminal"><PageWrapper><BailCalculatorPage /></PageWrapper></RoleRoute>} />
                <Route path="/fir" element={<RoleRoute role="criminal"><PageWrapper><FIRAnalyzerPage /></PageWrapper></RoleRoute>} />
                <Route path="/sample-matter" element={<RoleRoute role="criminal"><PageWrapper><SampleMatterPage /></PageWrapper></RoleRoute>} />
                <Route path="/notice-analyzer" element={<RoleRoute role="civil"><PageWrapper><NoticeAnalyzerPage /></PageWrapper></RoleRoute>} />
                <Route path="/forum-calculator" element={<RoleRoute role="civil"><PageWrapper><ForumCalculatorPage /></PageWrapper></RoleRoute>} />
                {/* Gated to civil in step with its sidebar entry (personaConfig
                    keys it off `forum_calculator`, whose capability set is
                    SUIT_PRACTICE_AREAS ⊂ the civil role). Keep the two in step:
                    a route guard narrower than the nav gate is a dead end. */}
                <Route path="/interest-calculator" element={<RoleRoute role="civil"><PageWrapper><InterestCalculatorPage /></PageWrapper></RoleRoute>} />
                <Route path="/civil-guide" element={<RoleRoute role="civil"><PageWrapper><CivilGuidePage /></PageWrapper></RoleRoute>} />
                <Route path="/cause-list" element={<PageWrapper><CauseListPage /></PageWrapper>} />
                <Route path="/bring-your-cases" element={<PageWrapper><BringYourCasesPage /></PageWrapper>} />
                <Route path="/hearing-prep" element={<RoleRoute role="criminal"><PageWrapper><HearingPrepPage /></PageWrapper></RoleRoute>} />
                <Route path="/day-book" element={<PageWrapper><AdvocateDiaryPage /></PageWrapper>} />
                {/* Shared shell, per-board ranking: the page picks /calendar/criminal
                    or /calendar/civil from the active role. */}
                <Route path="/calendar" element={<RoleRoute roles={['criminal', 'civil']}><PageWrapper><CriminalCalendarPage /></PageWrapper></RoleRoute>} />
                <Route path="/diary/:caseId" element={<PageWrapper><CaseDiaryPage /></PageWrapper>} />
                <Route path="/vault" element={<PageWrapper><DocumentVaultPage /></PageWrapper>} />
                <Route path="/research" element={<PageWrapper><ResearchPage /></PageWrapper>} />
                <Route path="/clients" element={<PageWrapper><ClientsListPage /></PageWrapper>} />
                <Route path="/clients/new" element={<PageWrapper><NewClientPage /></PageWrapper>} />
                <Route path="/workspace/:clientId" element={<PageWrapper><ClientWorkspacePage /></PageWrapper>} />
                <Route path="/workspace/:clientId/matters" element={<PageWrapper><ClientWorkspacePage /></PageWrapper>} />
                <Route path="/workspace/:clientId/matters/new" element={<PageWrapper><NewMatterPage /></PageWrapper>} />
                <Route path="/workspace/:clientId/matters/:matterId" element={<PageWrapper><MatterDetailPage /></PageWrapper>} />
                <Route path="/settings" element={<PageWrapper><SettingsPage /></PageWrapper>} />

                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <PageWrapper><AdminDashboard /></PageWrapper>
                    </AdminRoute>
                  }
                />
                {/* Support Desk for admins (a support user never reaches AppLayout —
                    see SupportShell). SupportRoute asks the backend, which also
                    honours the kill switch. */}
                <Route
                  path="/admin/support"
                  element={
                    <SupportRoute>
                      <PageWrapper><SupportDeskPage /></PageWrapper>
                    </SupportRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId/costs"
                  element={
                    <AdminRoute>
                      <PageWrapper><CostUserProfilePage /></PageWrapper>
                    </AdminRoute>
                  }
                />
                {/* Unknown URL: say so (QA Q54) — a silent redirect to "/" hid stale links. */}
                <Route path="*" element={<PageWrapper><NotFoundPage /></PageWrapper>} />
              </Routes>
            </AnimatePresence>
          </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}

/** True on first render if the URL carries the cross-app sign-out signal. */
function hasSignoutParam(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('signout') === '1';
  } catch {
    return false;
  }
}

function App() {
  const { user, loading } = useAuth();
  // Cross-app sign-out: corporate-web sends the user here with `?signout=1` after
  // its sign-out. Because Firebase persistence is per-origin, corporate's
  // sign-out doesn't clear OUR session — so we must sign out here too, otherwise
  // the corporate-only auto-redirect would immediately bounce the user back into
  // corporate (a sign-out that never sticks). We sign out, strip the param, and
  // land on the login screen.
  const [signingOut, setSigningOut] = useState(() => hasSignoutParam());
  useEffect(() => {
    if (!hasSignoutParam()) return;
    // Strip the param immediately so a refresh doesn't re-trigger sign-out.
    window.history.replaceState({}, '', window.location.pathname);
    // Fire the sign-out; do NOT clear `signingOut` here. We keep the neutral
    // screen up until `user` actually becomes null (below), otherwise a stale
    // render (signingOut=false but user not yet nulled by the auth listener)
    // could mount the gate and re-fire the corporate auto-redirect — bouncing
    // the user right back into corporate.
    void firebaseSignOut().catch(() => {
      /* ignore — the auth listener still transitions us to logged-out */
    });
  }, []);
  // Deterministically leave the "Signing out..." screen only once the auth
  // listener has cleared `user`. Now the fall-through lands on <LoginForm/>.
  useEffect(() => {
    if (signingOut && !user) setSigningOut(false);
  }, [signingOut, user]);
  // Redux profile is hydrated by useAuth from /auth/me. We need its
  // is_onboarded flag to drive the SignupWizard gate — the Firebase user
  // object doesn't carry it.
  const profile = useSelector((s: RootState) => s.auth.user);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const location = useLocation();

  const handleOnboardingComplete = useCallback(() => {
    setOnboardingComplete(true);
  }, []);
  const handleLegalAccepted = useCallback(() => {
    setLegalAccepted(true);
  }, []);

  // The policy pages are the ONLY pre-login routes in this app — always
  // public, checked before every other gate below (loading, auth, legal,
  // onboarding), so they render instantly regardless of session state.
  // Refund / shipping / contact are the pages Razorpay requires a merchant
  // site to carry (2026-10-18).
  if (PUBLIC_POLICY_PATHS.has(location.pathname)) {
    return (
      <Suspense fallback={<SealLoader />}>
        <Routes>
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (loading) {
    return (
      <SealLoader />
    );
  }

  // While the cross-app sign-out is in flight, show a neutral screen — do NOT
  // fall through to the app (which would let the corporate-only auto-redirect
  // fire before the sign-out completes and bounce the user back to corporate).
  if (signingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-chamber-rail">
        <div className="text-center">
          <ColumnMark className="h-12 w-auto mx-auto text-brass animate-pulse" />
          <p className="mt-4 text-sm font-instrument text-chamber-muted">Signing out...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LoginForm />
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  // Wait for Redux profile to hydrate before deciding the gate. Without
  // this guard we'd race-condition: brief render of the dashboard for a
  // not-yet-onboarded user during the useAuth → /auth/me round trip.
  if (!profile) {
    return (
      <SealLoader words={["Loading your workspace", "Preparing your counsel"]} />
    );
  }

  // Gate order lives in utils/appGate.ts (testable without Firebase).
  // Consent gate comes BEFORE onboarding — a lawyer should never see the
  // practice-area wizard before agreeing to how the platform (and its AI
  // output) may be used. Covers brand-new signups AND every existing user
  // with no acceptance on record (their terms_version starts null), so
  // there's no separate "retroactive" code path. A support user accepts the
  // terms too, then goes straight to the Support Desk shell.
  const gate = nextGate(profile, { legalAccepted, onboardingComplete });

  if (gate === 'legal') {
    return (
      <>
        <LegalGate onComplete={handleLegalAccepted} />
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  if (gate === 'support') {
    return (
      <Suspense fallback={<SealLoader />}>
        <SupportShell />
        <Toaster position="top-right" richColors closeButton />
      </Suspense>
    );
  }

  if (gate === 'wizard') {
    return (
      <>
        <SignupWizard onComplete={handleOnboardingComplete} />
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  return (
    <PreferencesProvider>
      <PracticeAreaGate>
        <ThemeProvider>
          <SidebarProvider>
            <WorkspaceProvider>
              <AppLayout />
              <Toaster position="top-right" richColors closeButton />
            </WorkspaceProvider>
          </SidebarProvider>
        </ThemeProvider>
      </PracticeAreaGate>
    </PreferencesProvider>
  );
}

/**
 * Gates the app behind the per-login workspace chooser — but ONLY when the
 * lawyer has more than one IN-APP role. Lives INSIDE PreferencesProvider so we
 * can set the session lens for backward-compat.
 *
 * Role logic (corporate is a separate app reached via SSO, not an in-app lens):
 *   corporate-ONLY (corporate is the user's sole role): auto SSO hand-off to
 *     corporate-web — the lawyer never sees the main-app dashboard.
 *   0 in-app roles, no corporate (legacy/unclassified): NO chooser — fall
 *     through to the shared tools. NEVER show an empty, unsatisfiable chooser.
 *   1 in-app role: auto-select it (write role + lens, mark chosen) — NO
 *     chooser, but the lawyer still runs the same TrialGate (phone-OTP +
 *     provisional trial registration, spec §9.3) that the >1-role chooser
 *     runs, since this is the majority onboarding path.
 *   >1 in-app roles: show the chooser once per browser session (sessionStorage).
 *     (A user with corporate PLUS in-app roles gets the chooser, which offers
 *     the Corporate card — they are NOT auto-redirected.)
 */
function PracticeAreaGate({ children }: { children: React.ReactNode }) {
  const profile = useSelector((s: RootState) => s.auth.user);
  const { setActivePracticeArea } = usePreferences();
  const allRoles = rolesOfUser(profile?.practice_areas);
  const inAppRoles = [...allRoles].filter((r): r is TrialGateRole => r !== 'corporate');
  // Corporate is the user's ONLY role → auto-redirect to corporate-web.
  // BUT not while their access is still awaiting an admin grant: the corporate
  // wall would refuse them on arrival, and a corporate-only user has no in-app
  // workspace to fall back to, so the redirect would strand them on a "no
  // access" screen with no way back. Hold them here and explain instead.
  const corporateAwaitingApproval = profile?.corporate_access === 'pending';
  const corporateOnly =
    allRoles.has('corporate') && inAppRoles.length === 0 && !corporateAwaitingApproval;
  // Lazy init: reflect "already chosen this session" on first render so we
  // never flash the dashboard before the chooser.
  const [chosen, setChosen] = useState(() => hasChosenPracticeAreaThisSession());
  // Once we kick off the SSO hand-off, render a redirect screen (not the app).
  const [redirecting, setRedirecting] = useState(false);
  // Set once the single-role auto-select effect below has persisted the role
  // + lens. Distinct from `chosen` so we can interpose the TrialGate BEFORE
  // flipping chosen (and thus before rendering children) — mirrors how the
  // >1-role chooser only calls setChosen(true) from its own onDone.
  const [autoSelectedRole, setAutoSelectedRole] = useState<TrialGateRole | null>(null);
  // One-shot guard: attempt the corporate hand-off exactly ONCE per mount. A ref
  // (not state) so it can't re-trigger the effect. Without it, a persistent
  // launch failure would loop forever (corporateOnly stays true, and every
  // redirecting→false transition would re-fire the mint), hammering the backend.
  const corporateAttemptedRef = useRef(false);

  useEffect(() => {
    // Corporate-only lawyer: hand off to the Corporate Suite immediately. If the
    // hand-off fails (URL not configured, mint error), fall through to the main
    // app rather than trapping the user on a blank redirect screen — and never
    // retry (the one-shot ref), so a misconfig can't loop on /auth/corporate-handoff.
    if (corporateOnly && !corporateAttemptedRef.current) {
      corporateAttemptedRef.current = true;
      setRedirecting(true);
      // Claim the corporate trial BEFORE handing off. A corporate-only lawyer
      // never mounts an in-app TrialGate (they leave for corporate-web by
      // SSO), so nothing used to register their trial — while the landing page
      // sold them Firm at ₹9,999 and Firm Max at ₹24,999 under a "30 days on
      // the Pro plan in full" promise. The backend always accepted
      // `corporate`; only the frontend never asked.
      //
      // Deliberately NOT awaited: the SSO hand-off must not wait on a billing
      // call. The SERVER registers the trial (GET /entitlements/me for the
      // practices chosen); if this call is lost it happens on the next visit.
      void ensureTrialOnServer();
      void launchCorporateSuite().catch(() => setRedirecting(false));
      return;
    }
    if (chosen || autoSelectedRole) return;
    // Several in-app roles, new tab of the SAME sign-in: restore the role
    // chosen in this sign-in instead of re-prompting (QA Q28). A new sign-in
    // never matches, so the "every login" prompt is unchanged.
    if (inAppRoles.length > 1) {
      const restored = restoreSameSignInRole(inAppRoles);
      if (restored === 'criminal' || restored === 'civil') {
        void setActivePracticeArea(ROLE_LENS[restored]);
        setChosen(true);
      }
      return;
    }
    // Single in-app role: auto-select — no chooser. Persist the active role
    // (axios sends it as X-Active-Role) and a representative session lens,
    // then hand off to the TrialGate (below) instead of marking chosen
    // immediately — the gate calls back into setChosen(true) when it's done.
    if (inAppRoles.length === 1) {
      const role = inAppRoles[0];
      try {
        sessionStorage.setItem('nyayabodh_active_role', role);
        sessionStorage.setItem('nyayabodh_practice_area_chosen', '1');
      } catch {
        /* ignore storage errors — don't trap the user */
      }
      void setActivePracticeArea(role === 'criminal' ? 'criminal' : 'civil_consumer');
      setAutoSelectedRole(role);
    }
    // 0 in-app roles (no corporate): leave chosen=false — we render children
    // anyway (below) so the user is never locked out behind an empty chooser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corporateOnly, chosen, autoSelectedRole, inAppRoles.length, setActivePracticeArea]);

  // Corporate-only AND awaiting an admin grant: they have no in-app workspace
  // to fall through to, so render the waiting state here rather than dropping
  // them into an empty app or bouncing them off the corporate wall.
  if (
    corporateAwaitingApproval &&
    allRoles.has('corporate') &&
    inAppRoles.length === 0
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-chamber-rail p-6">
        <div className="max-w-md text-center">
          <ColumnMark className="h-12 w-auto mx-auto text-brass" />
          <h1 className="mt-6 font-instrument text-xl text-chamber-panel">
            Your corporate workspace is awaiting approval
          </h1>
          <p className="mt-3 text-sm text-chamber-muted leading-relaxed">
            We have your request to use the Corporate Legal Suite. Access is
            being enabled account by account while the suite is in early
            release, so someone from our team reviews each one.
          </p>
          <p className="mt-3 text-sm text-chamber-muted leading-relaxed">
            You&rsquo;ll be able to sign in as soon as it&rsquo;s approved —
            nothing further is needed from you.
          </p>
        </div>
      </div>
    );
  }

  // Corporate-only: show a hand-off screen while the SSO nonce mints + navigates.
  if (corporateOnly && redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-chamber-rail">
        <div className="text-center">
          <ColumnMark className="h-12 w-auto mx-auto text-brass animate-pulse" />
          <p className="mt-4 text-sm font-instrument text-chamber-muted">Opening the Corporate Suite…</p>
        </div>
      </div>
    );
  }

  // Only the >1-role path renders the chooser. 0-role and 1-role fall through.
  if (!chosen && inAppRoles.length > 1) {
    // Restorable → the effect above enters the role on this render's commit;
    // render nothing for that frame rather than flashing the chooser.
    if (canRestoreSameSignInRole(inAppRoles)) return null;
    return <PracticeAreaChooser onDone={() => setChosen(true)} />;
  }

  // Single-role auto-select path: role/lens already persisted above — now run
  // the SAME trial gate the multi-role chooser runs before entering the app.
  if (!chosen && autoSelectedRole) {
    return <TrialGate role={autoSelectedRole} onDone={() => setChosen(true)} />;
  }

  return <>{children}</>;
}

export default App;
